import { AttackPattern, PatternState } from '../AttackPattern.js';

let nextStaccatoToken = 1;

/**
 * Staccato Volley (The Iron Cadence) — bullets that obey the conductor's
 * beat instead of physics: every ring it fires moves ONLY during the first
 * stretch of each beat, then freezes mid-air until the next downbeat. The
 * whole volley lurches forward in lockstep, so dodging becomes turn-based:
 * read the frozen lattice during the rest, commit during the hop. Distinct
 * from The Unblinking's Stutter Field (one stall, one re-aimed wake) — here
 * the stop-and-go IS the bullet's entire flight, synchronized to a global
 * beat that speeds up as the Cadence loses HP.
 *
 * Same pooled-reference bookkeeping as StutterField: bullets are tagged
 * with a per-cast token so a recycled slot re-fired by a layered pattern is
 * left alone, and bullets captured by a Grand Pause (`_grandPaused`) are
 * not steered until the caesura releases them.
 */
export class StaccatoVolley extends AttackPattern {
  constructor({ minPhase = 0, config }) {
    super({ name: 'Staccato Volley', telegraphDuration: 0.9, activeDuration: 5.4, minPhase });
    this.config = config;
  }

  onBegin(boss) {
    this._token = nextStaccatoToken++;
    this._bullets = [];
    // The conductor counts faster as the fight escalates.
    this._beatInterval = Math.max(0.38, this.config.beatInterval - boss.phase * 0.05);
    this._beatClock = 0;
    this._volleysFired = 0;
    this._baseAngle = Math.random() * Math.PI * 2;
    this._emittedBeat = false;
  }

  /** True during the opening stretch of the current beat — the only window its bullets move in. */
  get _inHop() {
    return this._beatClock < this._beatInterval * this.config.hopFraction;
  }

  onActiveTick(dt, boss, _player, pool) {
    this._beatClock += dt;
    if (this._beatClock >= this._beatInterval) {
      this._beatClock -= this._beatInterval;
      this._emittedBeat = false;
    }
    // One audible tick per downbeat (start of each hop window).
    if (!this._emittedBeat && this._inHop) {
      this._emittedBeat = true;
      boss.eventBus?.emit('cadence:beat');
      const maxVolleys = 2 + boss.phase;
      if (this._volleysFired < maxVolleys) this._fireVolley(boss, pool);
    }

    // Steer every still-live bullet of ours: full speed inside the hop
    // window (scaled up so the average travel speed over a whole beat is
    // exactly config.speed), dead stop outside it.
    const hopSpeed = this.config.speed / this.config.hopFraction;
    const warn = this._beatClock > this._beatInterval * 0.82; // about to lurch
    for (const entry of this._bullets) {
      const { bullet, angle } = entry;
      if (!bullet.active || bullet._staccatoToken !== this._token) continue;
      if (bullet._grandPaused) continue; // a Grand Pause owns it for now
      if (this._inHop) {
        bullet.vx = Math.cos(angle) * hopSpeed;
        bullet.vy = Math.sin(angle) * hopSpeed;
        bullet.color = '#cfe0ff';
      } else {
        bullet.vx = 0;
        bullet.vy = 0;
        bullet.color = warn ? '#ffffff' : '#7c92c4';
      }
      bullet.speed = Math.hypot(bullet.vx, bullet.vy);
    }
  }

  _fireVolley(boss, pool) {
    this._volleysFired++;
    this._baseAngle += 0.31; // each measure enters rotated — a spiralling score
    const count = this.config.ringCount + boss.phase * 3;
    for (let i = 0; i < count; i++) {
      const angle = this._baseAngle + (i / count) * Math.PI * 2;
      const bullet = pool.fire({
        x: boss.x,
        y: boss.y,
        vx: 0, // steered by the beat from the first tick — never free-flying
        vy: 0,
        radius: 2.6,
        damage: this.config.damage,
        color: '#cfe0ff',
        lifetime: 7,
      });
      bullet._staccatoToken = this._token;
      this._bullets.push({ bullet, angle });
    }
  }

  onEnd() {
    // Hand surviving bullets back to plain physics at their true average
    // speed — no frozen orphans lingering after the conductor moves on.
    for (const { bullet, angle } of this._bullets) {
      if (!bullet.active || bullet._staccatoToken !== this._token) continue;
      bullet._staccatoToken = null;
      bullet.vx = Math.cos(angle) * this.config.speed;
      bullet.vy = Math.sin(angle) * this.config.speed;
      bullet.speed = this.config.speed;
      bullet.color = '#cfe0ff';
    }
  }

  getVisual(boss) {
    if (this.state === PatternState.TELEGRAPH) {
      return { kind: 'cadenceBeat', x: boss.x, y: boss.y, beatT: 1 - this._timer / this.telegraphDuration, hop: false };
    }
    if (this.state === PatternState.ACTIVE) {
      return { kind: 'cadenceBeat', x: boss.x, y: boss.y, beatT: this._beatClock / this._beatInterval, hop: this._inHop };
    }
    return null;
  }
}
